const SECTION_PREFIX = 'shopify-section-';

// The nav carries the scale's position; every node colour, fill and emphasis is derived from it.
const PACK_POSITION_CLASSES = ['ffx-pdp__packs--at1', 'ffx-pdp__packs--at2', 'ffx-pdp__packs--at3'];

// Pointer travel before a press counts as a drag, below which a tap stays a plain link click.
const DRAG_THRESHOLD = 6;

class FoolFixProduct extends HTMLElement {
  #listeners = new AbortController();
  #requestController;
  #drag = null;
  #ignoreNextClick = false;
  #usedKeyboard = false;
  #stickyObserver;
  #stickyBar = null;

  connectedCallback() {
    const { signal } = this.#listeners;

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    window.addEventListener('pointermove', this.#handlePointerMove, { signal });
    window.addEventListener('pointerup', this.#handlePointerEnd, { signal });
    window.addEventListener('pointercancel', this.#handlePointerEnd, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });

    this.#setPackTabStop();
    this.#selectRequestedPurchaseOption();
    this.#setupStickyBar();
    this.#syncPurchaseOptions();
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#requestController?.abort();
    this.#stickyObserver?.disconnect();
    this.#stickyBar?.remove();
  }

  get #sellingPlanInput() {
    const input = this.querySelector('form[action*="/cart/add"] [data-selling-plan-input]');

    return input instanceof HTMLInputElement ? input : null;
  }

  get #selectedPurchaseType() {
    const input = this.querySelector('[data-purchase-type]:checked');

    return input instanceof HTMLInputElement ? input : null;
  }

  get #selectedSellingPlan() {
    const input = this.querySelector('[data-selling-plan-option]:checked');

    return input instanceof HTMLInputElement ? input : null;
  }

  get #planPriceData() {
    if (this.#selectedPurchaseType?.value !== 'subscription') return null;

    const input = this.#selectedSellingPlan;

    return input?.dataset.price ? input : null;
  }

  // The sticky bar is reparented to <body> during setup, so the selection summary it mirrors has to
  // be collected from both roots.
  #selectionNodes(selector) {
    const nodes = Array.from(this.querySelectorAll(selector));

    if (this.#stickyBar) {
      nodes.push(...this.#stickyBar.querySelectorAll(selector));
    }

    return nodes;
  }

  get #packLinks() {
    return Array.from(this.querySelectorAll('a[data-pack-link]'));
  }

  get #packScale() {
    return this.querySelector('[data-pack-scale]');
  }

  #handleChange = () => {
    queueMicrotask(() => this.#syncPurchaseOptions());
  };

  #handleClick = (event) => {
    const link = this.#closestPackLink(event.target);
    if (!link) return;

    if (this.#ignoreNextClick) {
      this.#ignoreNextClick = false;
      event.preventDefault();
      return;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const url = this.#packUrl(link);
    event.preventDefault();

    if (url.pathname === window.location.pathname) return;

    this.#renderProduct(url, true);
  };

  // Arrows only move focus, never select, so holding a key can't fire a request per step.
  #handleKeyDown = (event) => {
    const link = this.#closestPackLink(event.target);
    if (!link) return;

    this.#usedKeyboard = true;

    const links = this.#packLinks;
    const index = links.indexOf(link);
    let target = index;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        target = index + 1;
        break;

      case 'ArrowLeft':
      case 'ArrowDown':
        target = index - 1;
        break;

      case 'Home':
        target = 0;
        break;

      case 'End':
        target = links.length - 1;
        break;

      case ' ':
        event.preventDefault();
        link.click();
        return;

      default:
        return;
    }

    event.preventDefault();
    links[Math.min(Math.max(target, 0), links.length - 1)]?.focus();
  };

  #handleFocusIn = (event) => {
    const link = this.#closestPackLink(event.target);
    if (!link) return;

    this.#setPackTabStop(link);
    this.#showPackPosition(this.#packLinks.indexOf(link));
  };

  #handleFocusOut = (event) => {
    if (this.#drag || this.#closestPackLink(event.relatedTarget)) return;

    this.#resetPackPosition();
  };

  #handlePointerDown = (event) => {
    this.#ignoreNextClick = false;
    this.#usedKeyboard = false;

    if (!event.isPrimary || event.button !== 0) return;

    const rail = event.target instanceof Element ? event.target.closest('[data-pack-rail]') : null;
    if (!rail) return;

    this.#drag = { rail, pointerId: event.pointerId, startX: event.clientX, index: null, moved: false };
  };

  #handlePointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (!drag.moved) {
      if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD) return;

      drag.moved = true;
      drag.rail.classList.add('ffx-pdp__pack-rail--dragging');
    }

    drag.index = this.#nearestPackIndex(event.clientX);
    this.#showPackPosition(drag.index);
  };

  #handlePointerEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#drag = null;
    drag.rail.classList.remove('ffx-pdp__pack-rail--dragging');

    if (!drag.moved) return;

    this.#ignoreNextClick = true;

    const target = drag.index == null ? null : this.#packLinks[drag.index];

    if (!target || target.hasAttribute('aria-current')) {
      this.#resetPackPosition();
      return;
    }

    this.#renderProduct(this.#packUrl(target), true);
  };

  #handlePopState = () => {
    this.#renderProduct(new URL(window.location.href), false);
  };

  #closestPackLink(target) {
    const link = target instanceof Element ? target.closest('[data-pack-link]') : null;

    return link instanceof HTMLAnchorElement ? link : null;
  }

  #packUrl(link) {
    const url = new URL(link.href);
    const purchaseType = this.#selectedPurchaseType?.value;
    const sellingPlan = this.#selectedSellingPlan;

    if (purchaseType === 'subscription' && sellingPlan?.value) {
      url.searchParams.set('purchase', 'subscription');
      if (sellingPlan.dataset.planName) {
        url.searchParams.set('selling_plan_name', sellingPlan.dataset.planName);
      }
    } else if (purchaseType === 'one-time') {
      url.searchParams.set('purchase', 'one-time');
    }

    return url;
  }

  // One tab stop for the whole scale; arrows move between the stops inside it.
  #setPackTabStop(focused) {
    const links = this.#packLinks;
    if (links.length < 2) return;

    const tabStop = focused ?? links.find((link) => link.hasAttribute('aria-current')) ?? links[0];

    for (const link of links) {
      link.tabIndex = link === tabStop ? 0 : -1;
    }
  }

  #nearestPackIndex(clientX) {
    const nodes = this.querySelectorAll('[data-pack-node]');
    let nearest = 0;
    let shortest = Number.POSITIVE_INFINITY;

    for (const [index, node] of nodes.entries()) {
      const { left, width } = node.getBoundingClientRect();
      const distance = Math.abs(clientX - (left + width / 2));

      if (distance < shortest) {
        shortest = distance;
        nearest = index;
      }
    }

    return nearest;
  }

  #showPackPosition(index) {
    const scale = this.#packScale;
    const position = PACK_POSITION_CLASSES[index];
    if (!scale || !position) return;

    scale.classList.remove(...PACK_POSITION_CLASSES);
    scale.classList.add(position);
  }

  #resetPackPosition() {
    this.#showPackPosition(this.#packLinks.findIndex((link) => link.hasAttribute('aria-current')));
  }

  async #renderProduct(url, shouldPushState) {
    const currentSection = this.closest('.shopify-section');

    if (!(currentSection instanceof HTMLElement) || !currentSection.id.startsWith(SECTION_PREFIX)) {
      window.location.assign(url);
      return;
    }

    this.#requestController?.abort();
    this.#requestController = new AbortController();
    this.setAttribute('aria-busy', 'true');
    this.classList.add('ffx-product--loading');

    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', currentSection.id.replace(SECTION_PREFIX, ''));

    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.#requestController.signal,
      });

      if (!response.ok) throw new Error(`Product section request failed with ${response.status}`);

      const html = await response.text();
      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = responseDocument.getElementById(currentSection.id);

      if (!(nextSection instanceof HTMLElement)) {
        throw new Error('Product section missing from response');
      }

      if (shouldPushState) {
        window.history.pushState({ foolFixProduct: true }, '', url);
      }

      const replaceSection = () => currentSection.replaceWith(nextSection);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (document.startViewTransition && !prefersReducedMotion) {
        const transition = document.startViewTransition(replaceSection);
        await transition.updateCallbackDone;
      } else {
        replaceSection();
      }

      this.#updateDocumentMetadata(nextSection);
      this.#restartAppBlockScripts(nextSection);

      // Pointer users get no focus ring for a link they just tapped; keyboard users need the
      // equivalent stop under focus because the one they activated has left the DOM.
      if (shouldPushState && this.#usedKeyboard) {
        nextSection.querySelector('[data-pack-link][aria-current="page"]')?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.assign(url);
    }
  }

  #updateDocumentMetadata(section) {
    const productElement = section.querySelector('fool-fix-product');
    if (!(productElement instanceof HTMLElement)) return;

    const { documentTitle, canonicalUrl } = productElement.dataset;
    if (documentTitle) document.title = documentTitle;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical instanceof HTMLLinkElement && canonicalUrl) {
      canonical.href = canonicalUrl;
    }
  }

  #restartAppBlockScripts(section) {
    const scripts = section.querySelectorAll('.shopify-app-block script[src]');

    for (const script of scripts) {
      if (!(script instanceof HTMLScriptElement) || !script.parentElement) continue;

      const replacement = document.createElement('script');
      for (const attribute of script.attributes) {
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.textContent = script.textContent;
      script.replaceWith(replacement);
    }
  }

  #syncPurchaseOptions() {
    this.#syncSubscriptionDisclosure();
    this.#syncSellingPlanInput();
    this.#updateDisplayedPrice();
    this.#updatePackPrices();
  }

  #syncSubscriptionDisclosure() {
    const isSubscription = this.#selectedPurchaseType?.value === 'subscription';
    const panel = this.querySelector('[data-subscription-panel]');

    if (panel instanceof HTMLElement) {
      panel.setAttribute('aria-hidden', (!isSubscription).toString());
      panel.inert = !isSubscription;
    }

    for (const option of this.querySelectorAll('[data-selling-plan-option]')) {
      if (option instanceof HTMLInputElement) {
        option.disabled = !isSubscription;
      }
    }
  }

  #syncSellingPlanInput() {
    const input = this.#sellingPlanInput;
    if (!(input instanceof HTMLInputElement)) return;

    const selectedOption = this.#planPriceData;
    const planId = selectedOption?.value ?? '';

    input.value = planId;
    input.disabled = !planId;
  }

  #selectRequestedPurchaseOption() {
    const url = new URL(window.location.href);
    const requestedPurchase = url.searchParams.get('purchase');
    if (!requestedPurchase) return;

    const purchaseType = this.querySelector(
      `[data-purchase-type][value="${requestedPurchase === 'one-time' ? 'one-time' : 'subscription'}"]`,
    );

    if (purchaseType instanceof HTMLInputElement) {
      purchaseType.checked = true;
    }

    if (requestedPurchase === 'subscription') {
      const requestedPlanName = url.searchParams.get('selling_plan_name');
      const subscriptionOptions = Array.from(
        this.querySelectorAll('[data-selling-plan-option][data-plan-name]'),
      );
      const requestedOption =
        subscriptionOptions.find((option) => option.dataset.planName === requestedPlanName) ??
        subscriptionOptions[0];

      if (requestedOption instanceof HTMLInputElement) {
        requestedOption.checked = true;
      }
    }

    url.searchParams.delete('purchase');
    url.searchParams.delete('selling_plan_name');
    window.history.replaceState({}, '', url);
  }

  #updateDisplayedPrice() {
    const planData = this.#planPriceData;
    const currency = this.querySelector('[data-product-price-currency]');
    const amount = this.querySelector('[data-product-price-amount]');
    const summaryPrice = this.querySelector('[data-subscription-summary-price]');
    const benefit = this.querySelector('[data-subscription-benefit]');

    if (!currency || !amount) return;

    const nextCurrency = planData?.dataset.currency ?? this.dataset.oneTimeCurrency ?? '';
    const nextAmount = planData?.dataset.amount ?? this.dataset.oneTimeAmount ?? '';
    const nextPrice = planData?.dataset.price ?? this.dataset.oneTimePrice ?? '';

    // The subscription card always advertises what subscribing would cost, so it reads the checked
    // cadence rather than the active purchase type. Otherwise picking one-time strikes out the full
    // price and repeats it underneath as though subscribing saved nothing.
    const cadence = this.#selectedSellingPlan;
    const cadencePrice = cadence?.dataset.price ?? '';
    const cadenceSaving = cadence?.dataset.saving ?? '';

    if (currency.textContent !== nextCurrency) currency.textContent = nextCurrency;
    if (amount.textContent !== nextAmount) amount.textContent = nextAmount;
    if (summaryPrice && cadencePrice && summaryPrice.textContent !== cadencePrice) {
      summaryPrice.textContent = cadencePrice;
    }

    // The badge now sits in the cadence panel, so it follows the checked cadence and only hides for
    // a plan that carries no discount at all.
    if (benefit instanceof HTMLElement) {
      if (benefit.textContent !== cadenceSaving) benefit.textContent = cadenceSaving;
      benefit.classList.toggle('ffx-pdp__saving--hidden', !cadenceSaving);
    }

    // Each cadence radio and the one-time radio carry their own rendered summary wording, so the
    // active one is copied straight across rather than reassembled from parts in here.
    const nextMode =
      (planData ?? this.querySelector('[data-purchase-type][value="one-time"]'))?.dataset
        .selectionMode ?? '';

    for (const node of this.#selectionNodes('[data-selection-price]')) {
      if (node.textContent !== nextPrice) node.textContent = nextPrice;
    }

    if (nextMode) {
      for (const node of this.#selectionNodes('[data-selection-detail]')) {
        if (node.textContent !== nextMode) node.textContent = nextMode;
      }
    }
  }

  #updatePackPrices() {
    const selectedPlanName = this.#planPriceData?.dataset.planName;

    for (const link of this.#packLinks) {
      const price = link.querySelector('[data-pack-price]');
      const detail = link.querySelector('[data-pack-detail]');
      if (!price || !detail) continue;

      let nextPrice = link.dataset.oneTimePrice ?? '';
      let nextDetail = link.dataset.oneTimeDetail ?? '';

      if (selectedPlanName) {
        const plan = Array.from(link.querySelectorAll('[data-pack-plan]')).find(
          (option) => option.dataset.planName === selectedPlanName,
        );

        if (plan) {
          nextPrice = plan.dataset.price ?? nextPrice;
          nextDetail = plan.dataset.detail ?? nextDetail;
        } else {
          nextDetail = this.dataset.oneTimeOnly ?? nextDetail;
        }
      }

      if (price.textContent !== nextPrice) price.textContent = nextPrice;
      if (detail.textContent !== nextDetail) detail.textContent = nextDetail;
    }
  }

  #handleStickySubmit = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-sticky-submit]')) return;

    const form = this.querySelector('form[action*="/cart/add"]');
    const submitButton = form?.querySelector('.ffx-pdp__atc');

    if (form instanceof HTMLFormElement && submitButton instanceof HTMLButtonElement) {
      form.requestSubmit(submitButton);
    }
  };

  #setupStickyBar() {
    const buyRow = this.querySelector('.ffx-pdp__atcrow');
    const stickyBar = this.querySelector('[data-sticky-atc]');
    if (!buyRow || !(stickyBar instanceof HTMLElement)) return;

    // This element's view-transition-name makes it a containing block for fixed descendants, so the
    // bar has to be reparented to <body> to position against the viewport and clear later sections.
    document.body.append(stickyBar);
    this.#stickyBar = stickyBar;
    stickyBar.addEventListener('click', this.#handleStickySubmit, { signal: this.#listeners.signal });

    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const isVisible = !entry.isIntersecting;
      stickyBar.classList.toggle('ffx-pdp__sticky-atc--visible', isVisible);
      stickyBar.setAttribute('aria-hidden', (!isVisible).toString());
      stickyBar.inert = !isVisible;
    });
    this.#stickyObserver.observe(buyRow);
  }
}

if (!customElements.get('fool-fix-product')) {
  customElements.define('fool-fix-product', FoolFixProduct);
}
